/**
 * AiSuggestion — short read-out of the current week's plan on /plan.
 *
 * Backend cache 24h + frontend staleTime 12h: plans rarely shift mid-week
 * and the LLM payload is wider than a daily brief (whole week of
 * sessions), so we are stingier with refresh than AiBrief.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../../api";
import { AIGeneratedBadge } from "../AIGeneratedBadge";

type PlanSuggestionOut = {
  text: string;
  is_ai_generated: boolean;
  language: string;
  cached: boolean;
};

export function AiSuggestion() {
  const { t } = useTranslation("plan");
  const q = useQuery<PlanSuggestionOut>({
    queryKey: ["athlete-plan-suggestion"],
    queryFn: async () => {
      const r = await api.get("/athlete/ai/plan-suggestion");
      return r.data as PlanSuggestionOut;
    },
    staleTime: 12 * 60 * 60 * 1000,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <div
        data-testid="ai-suggestion-skeleton"
        className="indigo-card p-3"
      >
        <div className="h-4 w-2/3 rounded bg-bg-raised animate-pulse" />
      </div>
    );
  }
  if (q.isError || !q.data) return null;

  return (
    <div
      data-testid="ai-suggestion"
      className="flex items-start gap-2 indigo-card px-3 py-2.5"
    >
      <p className="flex-1 text-sm text-fg-primary leading-snug">{q.data.text}</p>
      {q.data.is_ai_generated && (
        <AIGeneratedBadge
          tooltip={t(
            "ai_suggestion_tooltip",
            "Резюме недели сформировано автоматически на основе вашего плана и состояния",
          )}
        />
      )}
    </div>
  );
}
